import {
  GENERATED_FILES_API_PATH,
  GENERATED_FILES_ARCHIVE_API_PATH,
  getGeneratedFileShareableProjectsPath,
  getGeneratedFileSharesPath,
} from "../constants/generated-files";
import type {
  IDownloadGeneratedFilesArchiveParams,
  IGeneratedFileShareProjectsResponse,
  IGeneratedFileSharesResponse,
  IGeneratedFilesResponse,
  IGetGeneratedFilesParams,
  IRemoveGeneratedFileSharesParams,
  IShareGeneratedFileParams,
  IShareGeneratedFileResponse,
} from "../types/generated-files";
import { chunkShareUuids } from "../utils/generated-file-shares";
import {
  buildGeneratedFilesArchiveQueryParams,
  buildGeneratedFilesQueryParams,
} from "../utils/generated-files";
import type { ApiClient } from "./client";

// NOTE: NSAI-4326 — the personal AI-generated files listing. Scoped
// server-side to the requesting user, so it carries no permission gate on
// either client.
//
// NSAI-6501 — plus sharing one file with teammates or whole projects (view and
// download only). The share routes are owner-only and gated server-side by the
// chat_share permissions.
export class GeneratedFilesApi {
  constructor(private readonly apiClient: ApiClient) {}

  async list(
    params: IGetGeneratedFilesParams = {},
  ): Promise<IGeneratedFilesResponse> {
    const response = await this.apiClient.get<IGeneratedFilesResponse>(
      GENERATED_FILES_API_PATH,
      { params: buildGeneratedFilesQueryParams(params) },
    );
    return response.data;
  }

  // NOTE: NSAI-5312 — one ZIP of the caller's generated files, fetched like
  // the single-file download (a blob with progress) so it goes through the
  // same auth and refresh handling. The server sends an exact Content-Length,
  // so `total` is known and the progress is a real percentage. Mobile does not
  // use this: it streams the same URL to disk with its native downloader.
  async downloadArchive(
    params: IDownloadGeneratedFilesArchiveParams,
  ): Promise<Blob> {
    const { request, onProgress, signal } = params;
    const response = await this.apiClient.get<Blob>(
      GENERATED_FILES_ARCHIVE_API_PATH,
      {
        params: buildGeneratedFilesArchiveQueryParams(request),
        responseType: "blob",
        onDownloadProgress: onProgress
          ? (event) => onProgress({ loaded: event.loaded, total: event.total })
          : undefined,
        ...(signal !== undefined && { signal }),
      },
    );

    return response.data;
  }

  async getShares(fileUuid: string): Promise<IGeneratedFileSharesResponse> {
    const response = await this.apiClient.get<IGeneratedFileSharesResponse>(
      getGeneratedFileSharesPath(fileUuid),
    );
    return response.data;
  }

  async getShareableProjects(
    fileUuid: string,
  ): Promise<IGeneratedFileShareProjectsResponse> {
    const response =
      await this.apiClient.get<IGeneratedFileShareProjectsResponse>(
        getGeneratedFileShareableProjectsPath(fileUuid),
      );
    return response.data;
  }

  async share({
    fileUuid,
    payload,
  }: IShareGeneratedFileParams): Promise<IShareGeneratedFileResponse> {
    const response = await this.apiClient.post<IShareGeneratedFileResponse>(
      getGeneratedFileSharesPath(fileUuid),
      payload,
    );
    return response.data;
  }

  // NOTE: The server takes at most GENERATED_FILE_SHARE_MAX_TARGETS share uuids
  // per request, and "Select all" on a long access list picks more than that —
  // so the removal goes out in batches, one after another, and the last answer
  // (every answer is the list that remains) is the one returned. A batch that
  // fails rejects the whole call; the batches before it stay removed, so the
  // callers refetch the access list on failure. An empty list still makes its
  // one request, leaving the answer to the server as before.
  async removeShares({
    fileUuid,
    shareUuids,
  }: IRemoveGeneratedFileSharesParams): Promise<IGeneratedFileSharesResponse> {
    const [firstBatch = [], ...restBatches] = chunkShareUuids({ shareUuids });

    let response = await this.removeShareBatch({
      fileUuid,
      shareUuids: firstBatch,
    });
    for (const batch of restBatches) {
      response = await this.removeShareBatch({ fileUuid, shareUuids: batch });
    }

    return response;
  }

  private async removeShareBatch({
    fileUuid,
    shareUuids,
  }: IRemoveGeneratedFileSharesParams): Promise<IGeneratedFileSharesResponse> {
    const response = await this.apiClient.delete<IGeneratedFileSharesResponse>(
      getGeneratedFileSharesPath(fileUuid),
      { data: { shareUuids } },
    );
    return response.data;
  }
}
